import React, { useState, useMemo } from "react";
import { User } from "../types";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { toast } from "sonner";
import { Plus, X, Search, Sparkles, Heart, FolderPlus, Check } from "lucide-react";
import {
  SKILL_CATEGORIES,
  INTEREST_CATEGORIES,
  CategoryGroup,
} from "../constants/skillsAndInterests.constants";

interface SkillsAndInterestsTabProps {
  userId: string;
  user: User;
  canEdit: boolean;
  onUserUpdate: (user: User) => void;
}

type ItemType = "skill" | "interest";

export const SkillsAndInterestsTab: React.FC<SkillsAndInterestsTabProps> = ({
  userId,
  user,
  canEdit,
  onUserUpdate,
}) => {
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<ItemType>("skill");
  const [searchQuery, setSearchQuery] = useState("");

  // Custom category creation
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewItem, setShowNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("");

  const skills = user.skills || [];
  const interests = user.interests || [];

  const openDialog = (type: ItemType) => {
    setDialogType(type);
    setSearchQuery("");
    setShowNewCategory(false);
    setShowNewItem(false);
    setNewCategoryName("");
    setNewItemName("");
    setNewItemCategory("");
    setDialogOpen(true);
  };

  const saveToApi = async (
    updatedSkills: { name: string; category: string }[],
    updatedInterests: { name: string; category: string }[]
  ) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/users/${userId}/skills-interests`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ skills: updatedSkills, interests: updatedInterests }),
        }
      );
      const data = await response.json();
      if (data.success) {
        onUserUpdate({ ...user, skills: updatedSkills, interests: updatedInterests });
        toast.success("Updated successfully");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Error saving changes");
    } finally {
      setSaving(false);
    }
  };

  const addItem = (name: string, category: string) => {
    const list = dialogType === "skill" ? [...skills] : [...interests];
    if (list.some((i) => i.name === name && i.category === category)) {
      toast.info("Already added");
      return;
    }
    list.push({ name, category });
    if (dialogType === "skill") {
      saveToApi(list, interests);
    } else {
      saveToApi(skills, list);
    }
  };

  const removeItem = (type: ItemType, name: string, category: string) => {
    if (type === "skill") {
      const updated = skills.filter((s) => !(s.name === name && s.category === category));
      saveToApi(updated, interests);
    } else {
      const updated = interests.filter((i) => !(i.name === name && i.category === category));
      saveToApi(skills, updated);
    }
  };

  const addCustomItem = () => {
    if (!newItemName.trim() || !newItemCategory.trim()) {
      toast.error("Please provide both a name and category");
      return;
    }
    addItem(newItemName.trim(), newItemCategory.trim());
    setNewItemName("");
    setNewItemCategory("");
    setShowNewItem(false);
  };

  const addNewCategoryItem = () => {
    if (!newCategoryName.trim() || !newItemName.trim()) {
      toast.error("Please provide both a category name and item name");
      return;
    }
    addItem(newItemName.trim(), newCategoryName.trim());
    setNewCategoryName("");
    setNewItemName("");
    setShowNewCategory(false);
  };

  // Build browsable categories with already-selected items marked
  const currentCategories: CategoryGroup[] = dialogType === "skill" ? SKILL_CATEGORIES : INTEREST_CATEGORIES;
  const currentList = dialogType === "skill" ? skills : interests;
  const selectedSet = useMemo(
    () => new Set(currentList.map((i) => `${i.category}::${i.name}`)),
    [currentList]
  );

  // Group existing items by category for display
  const groupByCategory = (items: { name: string; category: string }[]) => {
    const map: Record<string, string[]> = {};
    items.forEach((item) => {
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item.name);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  };

  const skillGroups = groupByCategory(skills);
  const interestGroups = groupByCategory(interests);

  return (
    <div className="space-y-6">
      {/* Skills Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-blue-500" />
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Skills
              </CardTitle>
              <Badge variant="outline" className="text-xs ml-1">
                {skills.length}
              </Badge>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => openDialog("skill")}>
                <Plus size={14} className="mr-1" /> Add Skill
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {skillGroups.length > 0 ? (
            <div className="space-y-4">
              {skillGroups.map(([category, items]) => (
                <div key={category}>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    {category}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {items.map((name) => (
                      <Badge key={name} variant="secondary" className="text-sm py-1 px-3 gap-1.5">
                        {name}
                        {canEdit && (
                          <button
                            onClick={() => removeItem("skill", name, category)}
                            className="ml-1 hover:text-destructive transition-colors"
                            disabled={saving}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No skills have been added yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Interests Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Heart size={18} className="text-pink-500" />
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Interests
              </CardTitle>
              <Badge variant="outline" className="text-xs ml-1">
                {interests.length}
              </Badge>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => openDialog("interest")}>
                <Plus size={14} className="mr-1" /> Add Interest
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {interestGroups.length > 0 ? (
            <div className="space-y-4">
              {interestGroups.map(([category, items]) => (
                <div key={category}>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    {category}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {items.map((name) => (
                      <Badge key={name} variant="secondary" className="text-sm py-1 px-3 gap-1.5">
                        {name}
                        {canEdit && (
                          <button
                            onClick={() => removeItem("interest", name, category)}
                            className="ml-1 hover:text-destructive transition-colors"
                            disabled={saving}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No interests have been added yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Add {dialogType === "skill" ? "Skill" : "Interest"}
            </DialogTitle>
          </DialogHeader>

          {!showNewCategory && !showNewItem ? (
            <>
              {/* Searchable list from categories */}
              <Command className="rounded-lg [&_[data-slot=command-input-wrapper]]:border-0 [&_[data-slot=command-input]]:ring-0 [&_[data-slot=command-input]]:outline-none [&_[data-slot=command-input]]:shadow-none [&_input]:ring-0 [&_input]:outline-none [&_input]:border-0 [&_input]:shadow-none" shouldFilter={true}>
                <CommandInput placeholder={`Search ${dialogType === "skill" ? "skills" : "interests"}...`} />
                <CommandList className="max-h-[400px]">
                  <CommandEmpty>
                    <div className="text-center py-4 space-y-2">
                      <p className="text-sm text-muted-foreground">No results found.</p>
                      <Button variant="outline" size="sm" onClick={() => setShowNewItem(true)}>
                        <Plus size={14} className="mr-1" /> Add custom {dialogType}
                      </Button>
                    </div>
                  </CommandEmpty>
                  {currentCategories.map((group) => (
                    <CommandGroup key={group.category} heading={group.category}>
                      {group.items.map((item) => {
                        const isSelected = selectedSet.has(`${group.category}::${item}`);
                        return (
                          <CommandItem
                            key={item}
                            value={`${group.category} ${item}`}
                            onSelect={() => {
                              if (isSelected) {
                                removeItem(dialogType, item, group.category);
                              } else {
                                addItem(item, group.category);
                              }
                            }}
                            className="flex items-center justify-between"
                          >
                            <span>{item}</span>
                            {isSelected && <Check size={14} className="text-green-600" />}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>

              {/* Bottom actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowNewItem(true)}
                >
                  <Plus size={14} className="mr-1" /> Custom {dialogType === "skill" ? "Skill" : "Interest"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowNewCategory(true)}
                >
                  <FolderPlus size={14} className="mr-1" /> New Category
                </Button>
              </div>
            </>
          ) : showNewItem ? (
            /* Add custom item under existing category */
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>{dialogType === "skill" ? "Skill" : "Interest"} Name</Label>
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder={`Enter ${dialogType} name`}
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={newItemCategory}
                    onChange={(e) => setNewItemCategory(e.target.value)}
                    placeholder="Search or type category"
                    className="pl-9"
                  />
                </div>
                {newItemCategory && (
                  <div className="max-h-32 overflow-y-auto border rounded-md">
                    {currentCategories
                      .filter((g) =>
                        g.category.toLowerCase().includes(newItemCategory.toLowerCase())
                      )
                      .map((g) => (
                        <button
                          key={g.category}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                          onClick={() => setNewItemCategory(g.category)}
                        >
                          {g.category}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowNewItem(false)}>
                  Back
                </Button>
                <Button onClick={addCustomItem} disabled={saving}>
                  Add
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* Add new category + item */
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>New Category Name</Label>
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                />
              </div>
              <div className="space-y-2">
                <Label>First {dialogType === "skill" ? "Skill" : "Interest"} in this category</Label>
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder={`Enter ${dialogType} name`}
                />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowNewCategory(false)}>
                  Back
                </Button>
                <Button onClick={addNewCategoryItem} disabled={saving}>
                  Create & Add
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
